import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

export default function SignUp() {
  const { signUp, signIn, signInWithGoogle } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // ── URL params (invite or fresh signup) ────────────────────
  const params      = new URLSearchParams(window.location.search);
  const inviteEmail = params.get('email') || '';
  const companyCode = params.get('companyCode') || '';
  const companyName = params.get('companyName') || '';
  const inviteRole  = params.get('role') || 'developer';
  const isInvited   = !!companyCode;

  // ── Form state ─────────────────────────────────────────────
  const [step,        setStep]        = useState<1 | 2>(1);
  const [fullName,    setFullName]    = useState('');
  const [email,       setEmail]       = useState(inviteEmail);
  const [orgName,     setOrgName]     = useState('');   // only used for fresh admin signup
  const [password,    setPassword]    = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [showPwd,     setShowPwd]     = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // ── Password strength ───────────────────────────────────────
  const strength = (() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 6)           s++;
    if (password.length >= 10)          s++;
    if (/[A-Z]/.test(password))         s++;
    if (/[0-9]/.test(password))         s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'][strength];
  const strengthClass = ['', 'weak',  'fair', 'good', 'strong', 'very-strong'][strength];

  // ── Step 1: validate basic info ────────────────────────────
  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) { showToast('Please enter your full name.', 'error'); return; }
    if (!email.trim())    { showToast('Please enter your email.', 'error'); return; }
    if (!isInvited && !orgName.trim()) { showToast('Please enter your company name.', 'error'); return; }
    setStep(2);
  };

  // ── Step 2: create account ─────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) { showToast('Password must be at least 6 characters.', 'error'); return; }
    if (password !== confirm) { showToast('Passwords do not match.', 'error'); return; }

    setLoading(true);

    if (isInvited) {
      // ── Invited user path: backend creates confirmed user ──
      try {
        const res = await fetch('/api/register-invited', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email:       email.trim().toLowerCase(),
            password,
            fullName:    fullName.trim(),
            companyCode: companyCode.toUpperCase(),
            role:        inviteRole,
          }),
        });

        const json = await res.json();

        if (!res.ok) {
          if (json.code === 'already_exists') {
            showToast('Account already exists. Signing you in…', 'error');
            await signIn(email, password);
            navigate('/');
          } else {
            showToast(json.error || 'Sign up failed. Please try again.', 'error');
          }
          setLoading(false);
          return;
        }

        // Auto sign-in — no email verification needed
        const { error: signInErr } = await signIn(email.trim().toLowerCase(), password);
        if (signInErr) {
          // Shouldn't happen, but fall back to login page
          showToast('Account created! Please sign in.', 'success');
          navigate('/login');
        } else {
          showToast(`Welcome to ${companyName || 'the team'}! 🎉`, 'success');
          navigate('/');
        }
      } catch (err: any) {
        showToast(err.message || 'Could not reach server. Is the backend running?', 'error');
      }
    } else {
      // ── Fresh admin signup path ──
      const { error } = await signUp(email, password, {
        fullName:    fullName.trim(),
        companyName: orgName.trim(),
      });
      if (error) {
        showToast(error.message || 'Sign up failed. Please try again.', 'error');
      } else {
        // Auto-confirm is enabled — sign in immediately
        const { error: signInErr } = await signIn(email.trim().toLowerCase(), password);
        if (signInErr) {
          showToast('Account created! Please sign in.', 'success');
          navigate('/login');
        } else {
          showToast('Welcome to Naipeak Issue Tracker!', 'success');
          navigate('/');
        }
      }
    }

    setLoading(false);
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    const { error } = await signInWithGoogle();
    setGoogleLoading(false);
    if (error) showToast(error.message || 'Google sign up failed.', 'error');
  };

  const roleName = inviteRole.charAt(0).toUpperCase() + inviteRole.slice(1);

  return (
    <div className="auth-page signup-page">
      <div className="signup-split">

        {/* ── Left branding panel ── */}
        <div className="signup-brand">
          <div className="signup-brand-inner">
            <div className="signup-brand-logo">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
              </svg>
            </div>
            <h2 className="signup-brand-title">IssueTracker</h2>
            {isInvited ? (
              <>
                <p className="signup-brand-desc">
                  You've been invited to join <strong style={{ color: '#fff' }}>{companyName || 'your team'}</strong> as a <strong style={{ color: '#fff' }}>{roleName}</strong>.
                </p>
                <ul className="signup-feature-list">
                  {[
                    'Your email is already verified',
                    'Role assigned automatically',
                    'Access your team\'s workspace instantly',
                    'No extra steps needed',
                  ].map(f => (
                    <li key={f} className="signup-feature-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><polyline points="20 6 9 17 4 12" /></svg>
                      {f}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <p className="signup-brand-desc">
                  The modern way to track issues, manage daily tasks, and ship faster as a team.
                </p>
                <ul className="signup-feature-list">
                  {[
                    'Track issues & bugs in one place',
                    'Daily task management with progress',
                    'Role-based team collaboration',
                    'Real-time deadline reminders',
                  ].map(f => (
                    <li key={f} className="signup-feature-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><polyline points="20 6 9 17 4 12" /></svg>
                      {f}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className="signup-form-panel">
          <div className="signup-form-inner">

            {/* Invitation badge */}
            {isInvited && (
              <div className="invite-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Invitation from <strong>{companyName || 'your team'}</strong> · {roleName}
              </div>
            )}

            {/* Step indicator */}
            <div className="signup-steps">
              <div className={`signup-step-dot ${step >= 1 ? 'active' : ''}`}>1</div>
              <div className={`signup-step-line ${step >= 2 ? 'active' : ''}`} />
              <div className={`signup-step-dot ${step >= 2 ? 'active' : ''}`}>2</div>
            </div>

            {/* ── Step 1 ── */}
            {step === 1 ? (
              <>
                <h1 className="auth-title" style={{ textAlign: 'left', marginBottom: 4 }}>
                  {isInvited ? 'Set up your account' : 'Create your account'}
                </h1>
                <p className="auth-subtitle" style={{ textAlign: 'left', marginBottom: 28 }}>
                  Step 1 of 2 — Your details
                </p>

                <form onSubmit={handleStep1} className="auth-form">
                  {/* Full name */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="fullName">Full name</label>
                    <div className="input-with-icon">
                      <input id="fullName" type="text" className="form-input"
                        placeholder="Jane Smith"
                        value={fullName} onChange={e => setFullName(e.target.value)}
                        autoComplete="name" autoFocus required />
                      <span className="input-icon-static">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
                        </svg>
                      </span>
                    </div>
                  </div>

                  {/* Email — readonly if invited */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="email">Work email</label>
                    <div className="input-with-icon">
                      <input id="email" type="email"
                        className={`form-input ${isInvited ? 'input-readonly' : ''}`}
                        placeholder="jane@yourcompany.com"
                        value={email} onChange={e => !isInvited && setEmail(e.target.value)}
                        readOnly={isInvited}
                        autoComplete="email" required />
                      <span className="input-icon-static">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                        </svg>
                      </span>
                    </div>
                    {isInvited && <p className="field-hint">This email was set by your invitation and cannot be changed.</p>}
                  </div>

                  {/* Company name — only for fresh admin signup */}
                  {!isInvited && (
                    <div className="form-group">
                      <label className="form-label" htmlFor="orgName">Company name</label>
                      <div className="input-with-icon">
                        <input id="orgName" type="text" className="form-input"
                          placeholder="Acme Inc."
                          value={orgName} onChange={e => setOrgName(e.target.value)}
                          required />
                        <span className="input-icon-static">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                            <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
                          </svg>
                        </span>
                      </div>
                      <p className="field-hint">You'll be the admin. Team members join via invite link.</p>
                    </div>
                  )}

                  <button type="submit" className="btn btn-primary btn-full btn-lg">
                    Continue
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                    </svg>
                  </button>

                  {/* Google OAuth only for fresh signups */}
                  {!isInvited && (
                    <>
                      <div className="auth-divider"><span>or</span></div>
                      <button type="button" className="btn btn-google btn-full" onClick={handleGoogle} disabled={googleLoading}>
                        {googleLoading ? <span className="btn-spinner btn-spinner-dark" /> : (
                          <svg viewBox="0 0 24 24" width="18" height="18">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        )}
                        Continue with Google
                      </button>
                    </>
                  )}
                </form>
              </>
            ) : (
              /* ── Step 2 ── */
              <>
                <button className="back-btn" style={{ marginBottom: 20 }} onClick={() => setStep(1)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
                  </svg>
                  Back
                </button>

                <h1 className="auth-title" style={{ textAlign: 'left', marginBottom: 4 }}>Set your password</h1>
                <p className="auth-subtitle" style={{ textAlign: 'left', marginBottom: 28 }}>
                  Step 2 of 2 — Almost there, <strong>{fullName.split(' ')[0]}</strong>!
                </p>

                {/* Summary card */}
                <div className="signup-summary-card">
                  <div className="summary-row">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    {fullName}
                  </div>
                  <div className="summary-row">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                    {email}
                  </div>
                  <div className="summary-row">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" /></svg>
                    {isInvited
                      ? <span>{companyName || 'Your team'} · <span className="summary-role-badge">{roleName}</span></span>
                      : orgName}
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="form-group">
                    <label className="form-label" htmlFor="password">Password</label>
                    <div className="input-with-icon">
                      <input id="password" type={showPwd ? 'text' : 'password'}
                        className="form-input" placeholder="At least 6 characters"
                        value={password} onChange={e => setPassword(e.target.value)}
                        autoComplete="new-password" autoFocus required />
                      <button type="button" className="input-icon-btn" onClick={() => setShowPwd(!showPwd)} tabIndex={-1}>
                        {showPwd ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                            <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {password && (
                      <div className="password-strength">
                        <div className="strength-bars">
                          {[1,2,3,4,5].map(i => (
                            <div key={i} className={`strength-bar ${i <= strength ? strengthClass : ''}`} />
                          ))}
                        </div>
                        <span className={`strength-label ${strengthClass}`}>{strengthLabel}</span>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="confirm">Confirm password</label>
                    <input id="confirm" type={showPwd ? 'text' : 'password'}
                      className={`form-input ${confirm && confirm !== password ? 'input-error' : confirm && confirm === password ? 'input-success' : ''}`}
                      placeholder="Re-enter password"
                      value={confirm} onChange={e => setConfirm(e.target.value)}
                      autoComplete="new-password" required />
                    {confirm && confirm !== password && <span className="form-error-text">Passwords don't match</span>}
                    {confirm && confirm === password && (
                      <span className="form-success-text">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><polyline points="20 6 9 17 4 12" /></svg>
                        Passwords match
                      </span>
                    )}
                  </div>

                  <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
                    {loading ? (
                      <><span className="btn-spinner" /> {isInvited ? 'Joining team…' : 'Creating account…'}</>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                          <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                        </svg>
                        {isInvited ? 'Join Team' : 'Create Account'}
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            <p className="auth-switch" style={{ marginTop: 20 }}>
              Already have an account?{' '}
              <Link to="/login" className="auth-link">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
